import { capitalize } from 'vue';

import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import BudgetModes from '@/common/enums/budget-modes.enum';
import { Plan, PurchaseTypeOptions } from '@/common/models';
import CreativeFormatOptions from '@/common/models/CreativeFormatOptions';
import CreativeOrientationOptions from '@/common/models/CreativeOrientationOptions';
import { convertRangesToDaypart, daypartToRanges, parseRanges } from '@/common/models/Dayparting';
import EntityProperty from '@/common/models/EntityProperty';
import {
  Filter,
  FilterCollection,
  FilterComparisonOperator,
  FilterLogicalOperator,
  QueryParams,
} from '@/common/models/query-params';
import { FullDaypart, InvalidDaypart } from '@/globals';
import {
  useAccounts,
  useAdvertisers,
  useMediaOwners,
  useMediaTypes,
  useNetworks,
} from '@/org-app/services/tanstack/queries';
import { useUserOrganizations } from '@/org-app/services/tanstack/queries/userOrganizations.queries';
import { runQueryInScope } from '@/org-app/services/tanstack/queryUtils';
import rootStore from '@/store';
import {
  UPDATE_PLAN_ACCOUNT_ID,
  UPDATE_PLAN_ADDURATION,
  UPDATE_PLAN_ADVERTISER_ID,
  UPDATE_PLAN_BUDGET,
  UPDATE_PLAN_BUDGET_MODE,
  UPDATE_PLAN_BUY_FROM_DEALS_ENABLED,
  UPDATE_PLAN_CREATIVEFORMATEXCLUSIONS,
  UPDATE_PLAN_CREATIVEFORMATS,
  UPDATE_PLAN_CREATIVEORIENTATIONEXCLUSIONS,
  UPDATE_PLAN_CREATIVEORIENTATIONS,
  UPDATE_PLAN_DAYPART,
  UPDATE_PLAN_DEMOGRAPHIC_TARGET_IDS,
  UPDATE_PLAN_ENDDATE,
  UPDATE_PLAN_GEOFENCE_GROUP_EXCLUSION_IDS,
  UPDATE_PLAN_GEOFENCE_GROUP_IDS,
  UPDATE_PLAN_LOCATIONIDS,
  UPDATE_PLAN_MARKET_IDS,
  UPDATE_PLAN_MARKETEXCLUSIONIDS,
  UPDATE_PLAN_MAXBIDCPM,
  UPDATE_PLAN_MEDIATYPEEXCLUSIONIDS,
  UPDATE_PLAN_MEDIATYPEIDS,
  UPDATE_PLAN_MINSECONDSBETWEENPLAYS,
  UPDATE_PLAN_NAME,
  UPDATE_PLAN_NETWORKEXCLUSIONIDS,
  UPDATE_PLAN_NETWORKIDS,
  UPDATE_PLAN_PACKAGEGROUP_DEALS,
  UPDATE_PLAN_SITEEXCLUSIONIDS,
  UPDATE_PLAN_SITEIDS,
  UPDATE_PLAN_STARTDATE,
  UPDATE_PLAN_TARGETIMPRESSIONS,
  UPDATE_PLAN_UNITEXCLUSIONIDS,
  UPDATE_PLAN_UNITIDS,
  UPDATE_PLAN_UNITPACKEXCLUSIONIDS,
  UPDATE_PLAN_UNITPACKIDS,
  UPDATE_PLAN_UNITTAGEXCLUSIONS,
  UPDATE_PLAN_UNITTAGS,
} from '@/store/modules/models/mutation-types';
import rootState from '@/store/state';
import i18n from '@/vueI18n';

export const getEntityProperties = () => {
  const type = 'Plan';

  const action = (plan) => rootStore.dispatch('orgApp/updatePlan', { instance: plan, showLoader: false });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getExtraValidation = (plan: Plan) => {
    const extras = {};
    return extras;
  };

  const getMediaOwners = () => runQueryInScope(useMediaOwners);

  //
  // Logic to fetch markets, only one call will be performed.
  //
  const getMarkets = async () => {
    if (Plan.isGettingMarkets) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!Plan.isGettingMarkets && Plan.marketsCache != null) {
            clearInterval(interval);
            resolve(Plan.marketsCache);
          }
        }, 500);
      });
    }
    Plan.isGettingMarkets = true;
    const mediaOwnerCountryRoots = await rootStore.dispatch('orgApp/getSupplierCountryRoots', { showLoader: false });
    const allMediaOwnerCountryIDs: number[][] = Object.values(mediaOwnerCountryRoots);
    const countryIDs: number[] = Array.from(new Set(allMediaOwnerCountryIDs.reduce((mem, x) => [...mem, ...x], [])));
    let ownerIDs: number[] = [];
    const { isSupplyOrganization } = rootState.orgApp.organization;
    if (isSupplyOrganization) {
      ownerIDs = [rootState.orgApp.organization.id];
    } else {
      const mediaOwners = await getMediaOwners();
      ownerIDs = mediaOwners.map((mediaOwner) => mediaOwner.id);
    }
    Plan.marketsCache = await rootStore.dispatch('orgApp/getMarketsWithInventory', {
      ownerIDs,
      countryIDs,
      showLoader: false,
    });
    Plan.isGettingMarkets = false;
    return Plan.marketsCache;
  };

  //
  // Logic to fetch networks, only one call will be performed.
  //
  const getNetworks = () => runQueryInScope(useNetworks);

  //
  // Logic to fetch mediaTypes, only one call will be performed.
  //
  const getMediaTypes = () => runQueryInScope(useMediaTypes);

  //
  // Logic to fetch unitPacks, only one call will be performed.
  //
  const getUnitPacks = async () => {
    if (Plan.isGettingUnitPacks) {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!Plan.isGettingUnitPacks && Plan.unitPacksCache != null) {
            clearInterval(interval);
            resolve(Plan.unitPacksCache);
          }
        }, 100);
      });
    }
    const queryParams = QueryParams.all(FilterCollection.for('active', [false]));
    Plan.isGettingUnitPacks = true;
    Plan.unitPacksCache = await rootStore.dispatch('orgApp/getUnitPacks', { queryParams, showLoader: false });
    Plan.isGettingUnitPacks = false;
    return Plan.unitPacksCache;
  };

  const budgetMode = new EntityProperty({
    label: capitalize(i18n.global.t('messages.deliverOn', { n: 2 })),
    property: 'budget_mode',
    type: TableColumnTypes.string,
    editablePropertyName: 'budgetMode',
    source: () => [
      {
        id: BudgetModes.BUDGET,
        name: capitalize(i18n.global.t('names.adSpend')),
      },
      {
        id: BudgetModes.IMPRESSIONS,
        name: capitalize(i18n.global.t('names.impression', { n: 2 })),
      },
    ],
  });

  const getLocationEntityProperty = (labelKey: string, level: number, isExclusion = false) => {
    return new EntityProperty({
      label: capitalize(i18n.global.t(labelKey, { n: 2 })),
      property: isExclusion ? 'location_exclusion_ids' : 'location_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: isExclusion ? 'locationExclusionIDs' : 'locationIDs',
      mutation: UPDATE_PLAN_LOCATIONIDS,
      editableClassName: type,
      source: ({ queryParams = QueryParams.all(FilterCollection.for('level', [level])) }) => {
        return rootStore.dispatch('orgApp/getLocations', queryParams);
      },
      action,
      preMutationTransform: (id) => (id != null ? [Number.parseInt(id, 10)] : []),
      getExtraValidation,
      sourceQueryParamsForReadOnly: (entity: Plan) =>
        QueryParams.all(
          new FilterCollection(
            [
              new Filter('level', [level]),
              new Filter(
                'location_id',
                isExclusion ? entity.locationExclusionIDs : entity.locationIDs,
                FilterComparisonOperator.IN,
              ),
            ],
            FilterLogicalOperator.AND,
          ),
        ),
      ignoreUnmatchedValuesInSource: true,
    });
  };

  Plan.entityPropertiesCached = {
    id: new EntityProperty({
      label: capitalize(i18n.global.t('names.planID', { n: 1 })),
      type: TableColumnTypes.string,
      property: 'plan_id',
      editablePropertyName: 'planID',
    }),
    name: new EntityProperty({
      label: capitalize(i18n.global.t('names.plan', { n: 1 })),
      property: 'name',
      type: TableColumnTypes.string,
      editablePropertyName: 'name',
      mutation: UPDATE_PLAN_NAME,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    startDate: new EntityProperty({
      label: capitalize(i18n.global.t('names.start', { n: 1 })),
      property: 'start_date',
      type: TableColumnTypes.date,
      editablePropertyName: 'startDate',
      mutation: UPDATE_PLAN_STARTDATE,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    endDate: new EntityProperty({
      label: capitalize(i18n.global.t('names.end', { n: 1 })),
      property: 'end_date',
      type: TableColumnTypes.date,
      editablePropertyName: 'endDate',
      mutation: UPDATE_PLAN_ENDDATE,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    account: new EntityProperty({
      label: capitalize(i18n.global.t('names.account', { n: 1 })),
      property: 'account.name',
      type: TableColumnTypes.integer,
      source: () => runQueryInScope(useAccounts),
      sourceFilterProperty: 'account_id',
      editablePropertyName: 'accountID',
      mutation: UPDATE_PLAN_ACCOUNT_ID,
      editableClassName: type,
      postMutationCallback: (value, entity) => {
        entity.account = value;
      },
    }),
    advertiser: new EntityProperty({
      label: capitalize(i18n.global.t('names.advertiser', { n: 1 })),
      property: 'advertiser.name',
      type: TableColumnTypes.integer,
      source: () => runQueryInScope(useAdvertisers),
      sourceFilterProperty: 'advertiser_id',
      editablePropertyName: 'advertiserID',
      mutation: UPDATE_PLAN_ADVERTISER_ID,
      editableClassName: type,
      postMutationCallback: (value, entity) => {
        entity.advertiser = value;
      },
      isInlineEditingAvailable: false,
    }),
    budgetMode,
    budget: new EntityProperty({
      label: capitalize(i18n.global.t('names.budget', { n: 1 })),
      property: 'budget',
      type: TableColumnTypes.currency,
      editablePropertyName: 'budget',
      mutation: UPDATE_PLAN_BUDGET,
      editableClassName: type,
      action,
      getExtraValidation,
      isEditableHandler: (li) => li.isBudgetEditable,
      preventEditingEntityProperties: ['targeted_impressions'],
      preMutationTransform: (x) => (x != null ? Number.parseFloat(x) : null),
      postMutationCallback: (value, entity) => {
        if (value == null) return;
        rootStore.commit(UPDATE_PLAN_BUDGET_MODE, {
          plan: entity,
          value: BudgetModes.BUDGET,
        });
      },
      mutatesProperties: [budgetMode],
    }),
    targetImpressions: new EntityProperty({
      label: capitalize(i18n.global.t('messages.impressionGoal')),
      property: 'targeted_impressions',
      type: TableColumnTypes.integer,
      editablePropertyName: 'targetImpressions',
      mutation: UPDATE_PLAN_TARGETIMPRESSIONS,
      editableClassName: type,
      action,
      getExtraValidation,
      isEditableHandler: (li) => li.isMarketTargetedImpressionsEditable,
      preventEditingEntityProperties: ['budget'],
      preMutationTransform: (x) => (x != null ? Number.parseInt(x, 10) : null),
      postMutationCallback: (value, entity) => {
        if (value == null) return;
        rootStore.commit(UPDATE_PLAN_BUDGET_MODE, {
          plan: entity,
          value: BudgetModes.IMPRESSIONS,
        });
      },
      mutatesProperties: [budgetMode],
    }),
    purchaseType: new EntityProperty({
      label: capitalize(i18n.global.t('names.purchaseType', { n: 1 })),
      property: 'purchase_type',
      type: TableColumnTypes.string,
      editablePropertyName: 'purchaseType',
      source: PurchaseTypeOptions.planOptions().map((x) => ({
        id: x.value,
        name: x.name,
        filters: x.filters,
      })),
    }),
    minSecondsBetweenPlays: new EntityProperty({
      label: capitalize(i18n.global.t('names.frequencyCap', { n: 1 })),
      property: 'min_seconds_between_plays',
      type: TableColumnTypes.integer,
      editablePropertyName: 'minSecondsBetweenPlays',
      mutation: UPDATE_PLAN_MINSECONDSBETWEENPLAYS,
      editableClassName: type,
      action,
      getExtraValidation,
      defaultValue: 0,
    }),
    modifiedOn: new EntityProperty({
      label: capitalize(i18n.global.t('messages.lastModifiedOn')),
      property: 'modified_on_utc',
      type: TableColumnTypes.datetime,
    }),
    createdBy: new EntityProperty({
      label: capitalize(i18n.global.t('messages.createdBy')),
      property: 'created_by',
      type: TableColumnTypes.list,
      source: () => runQueryInScope(useUserOrganizations),
      sourceFilterProperty: 'created_by',
      sourceLabel: 'email',
      sourceID: 'userID',
    }),
    createdOn: new EntityProperty({
      label: capitalize(i18n.global.t('messages.createdOn')),
      property: 'created_on_utc',
      type: TableColumnTypes.datetime,
    }),
    status: new EntityProperty({
      label: capitalize(i18n.global.t('names.status', { n: 1 })),
      property: 'plan_approvals',
      type: TableColumnTypes.string,
    }),
    sharedPlanURL: new EntityProperty({
      label: capitalize(i18n.global.t('names.sharedPlan', { n: 1 })),
      type: TableColumnTypes.boolean,
      property: 'sharedplan_id',
      checkForEmptyness: true,
    }),
    campaignLinkedURL: new EntityProperty({
      label: capitalize(i18n.global.t('names.campaignLinked', { n: 1 })),
      type: TableColumnTypes.boolean,
      property: 'campaigns',
      checkForEmptyness: { customComparisonValue: [null] },
    }),
    adDurationType: new EntityProperty({
      label: capitalize(i18n.global.t('names.adDurationType', { n: 1 })),
      property: 'adDurationTypeDescription',
      type: TableColumnTypes.list,
      action,
      getExtraValidation,
    }),
    adDuration: new EntityProperty({
      label: capitalize(i18n.global.t('names.adDuration', { n: 1 })),
      property: 'ad_duration',
      type: TableColumnTypes.integer,
      editablePropertyName: 'adDuration',
      mutation: UPDATE_PLAN_ADDURATION,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    isCPMFromRateCard: new EntityProperty({
      label: capitalize(i18n.global.t('messages.isCPMFromRateCard')),
      property: 'is_cpm_from_rate_card',
      type: TableColumnTypes.boolean,
      editablePropertyName: 'isCPMFromRateCard',
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    networkFrequencyCaps: new EntityProperty({
      label: capitalize(i18n.global.t('messages.enableNetworkFrequencyCap')),
      property: 'network_frequency_caps',
      type: TableColumnTypes.list,
      editablePropertyName: 'networkFrequencyCaps',
      editableClassName: type,
      editablePropertyAcceptsMultipleValues: true,
      action,
      getExtraValidation,
    }),
    campaignCustomFields: new EntityProperty({
      label: capitalize(i18n.global.t('names.customField', { n: 2 })),
      property: 'campaignCustomFields',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'campaignCustomFields',
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    isMeasurementRequested: new EntityProperty({
      label: capitalize(i18n.global.t('actions.includeLiftAnalysis', { n: 2 })),
      property: 'measurement_requested,',
      type: TableColumnTypes.boolean,
      editablePropertyName: 'isMeasurementRequested',
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    markets: new EntityProperty({
      label: capitalize(i18n.global.t('names.market', { n: 2 })),
      property: 'market_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMarkets,
      sourceFilterProperty: 'market_id',
      editablePropertyName: 'marketIDs',
      mutation: UPDATE_PLAN_MARKET_IDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    marketExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.marketExclusion', { n: 2 })),
      property: 'market_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMarkets,
      sourceFilterProperty: 'market_id',
      editablePropertyName: 'marketExclusionIDs',
      mutation: UPDATE_PLAN_MARKETEXCLUSIONIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    networks: new EntityProperty({
      label: capitalize(i18n.global.t('names.network', { n: 2 })),
      property: 'network_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getNetworks,
      sourceFilterProperty: 'network_id',
      editablePropertyName: 'networkIDs',
      mutation: UPDATE_PLAN_NETWORKIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    networkExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.networkExclusion', { n: 2 })),
      property: 'network_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getNetworks,
      sourceFilterProperty: 'network_id',
      editablePropertyName: 'networkExclusionIDs',
      mutation: UPDATE_PLAN_NETWORKEXCLUSIONIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    unitPacks: new EntityProperty({
      label: capitalize(i18n.global.t('names.unitPack', { n: 2 })),
      property: 'unitpack_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getUnitPacks,
      sourceFilterProperty: 'unitpack_id',
      editablePropertyName: 'unitPackIDs',
      mutation: UPDATE_PLAN_UNITPACKIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    unitPackExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.unitPackExclusion', { n: 2 })),
      property: 'unitpack_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getUnitPacks,
      sourceFilterProperty: 'unitpack_id',
      editablePropertyName: 'unitPackExclusionIDs',
      mutation: UPDATE_PLAN_UNITPACKEXCLUSIONIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    mediaTypes: new EntityProperty({
      label: capitalize(i18n.global.t('names.venueType', { n: 2 })),
      property: 'mediatype_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMediaTypes,
      sourceFilterProperty: 'mediatype_id',
      sourceGroupValues: 'children',
      sourceGroupLabel: 'name',
      editablePropertyName: 'mediaTypeIDs',
      mutation: UPDATE_PLAN_MEDIATYPEIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    mediaTypeExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.venueTypeExclusion', { n: 2 })),
      property: 'mediatype_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      source: getMediaTypes,
      sourceFilterProperty: 'mediatype_id',
      sourceGroupValues: 'children',
      sourceGroupLabel: 'name',
      editablePropertyName: 'mediaTypeExclusionIDs',
      mutation: UPDATE_PLAN_MEDIATYPEEXCLUSIONIDS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    creativeOrientations: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeOrientation', { n: 2 })),
      property: 'creative_orientations',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeOrientations',
      mutation: UPDATE_PLAN_CREATIVEORIENTATIONS,
      editableClassName: type,
      source: CreativeOrientationOptions.options(),
      action,
      getExtraValidation,
    }),
    creativeOrientationExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeOrientationExclusion', { n: 2 })),
      property: 'creative_orientation_exclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeOrientationExclusions',
      mutation: UPDATE_PLAN_CREATIVEORIENTATIONEXCLUSIONS,
      editableClassName: type,
      source: CreativeOrientationOptions.options(),
      action,
      getExtraValidation,
    }),
    creativeFormats: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeFormat', { n: 2 })),
      property: 'creative_formats',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeFormats',
      mutation: UPDATE_PLAN_CREATIVEFORMATS,
      editableClassName: type,
      source: CreativeFormatOptions.options(),
      action,
      getExtraValidation,
    }),
    creativeFormatExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.creativeFormatExclusion', { n: 2 })),
      property: 'creative_format_exclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'creativeFormatExclusions',
      mutation: UPDATE_PLAN_CREATIVEFORMATEXCLUSIONS,
      editableClassName: type,
      source: CreativeFormatOptions.options(),
      action,
      getExtraValidation,
    }),
    unitTags: new EntityProperty({
      label: capitalize(i18n.global.t('names.tag', { n: 2 })),
      property: 'unit_tags',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitTags',
      mutation: UPDATE_PLAN_UNITTAGS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    unitTagExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.tagExclusion', { n: 2 })),
      property: 'unit_tag_exclusions',
      type: TableColumnTypes.string,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitTagExclusions',
      mutation: UPDATE_PLAN_UNITTAGEXCLUSIONS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    countries: getLocationEntityProperty('names.country', 0),
    regions: getLocationEntityProperty('names.region', 1),
    cities: getLocationEntityProperty('names.city', 3),
    countryExclusions: getLocationEntityProperty('names.countryExclusion', 0, true),
    regionExclusions: getLocationEntityProperty('names.regionExclusion', 1, true),
    cityExclusions: getLocationEntityProperty('names.cityExclusion', 3, true),
    sites: new EntityProperty({
      label: capitalize(i18n.global.t('names.site', { n: 2 })),
      property: 'site_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'siteIDs',
      mutation: UPDATE_PLAN_SITEIDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getSites', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: Plan) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('site_id', entity.siteIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    siteExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.siteExclusion', { n: 2 })),
      property: 'site_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'siteExclusionIDs',
      mutation: UPDATE_PLAN_SITEEXCLUSIONIDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getSites', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: Plan) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('site_id', entity.siteExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    screens: new EntityProperty({
      label: capitalize(i18n.global.t('names.screen', { n: 2 })),
      property: 'unit_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitIDs',
      mutation: UPDATE_PLAN_UNITIDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getUnits', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: Plan) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('unit_id', entity.unitIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
          ['unit_id', 'external_id', 'name'],
        ),
      action,
      getExtraValidation,
    }),
    screenExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.screenExclusion', { n: 2 })),
      property: 'unit_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'unitExclusionIDs',
      mutation: UPDATE_PLAN_UNITEXCLUSIONIDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getUnits', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: Plan) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('unit_id', entity.unitExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
          ['unit_id', 'external_id', 'name'],
        ),
      action,
      getExtraValidation,
    }),
    proximityGeofenceGroups: new EntityProperty({
      label: capitalize(i18n.global.t('names.proximityGeofenceGroup', { n: 2 })),
      property: 'geofencegroup_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'geofenceGroupIDs',
      mutation: UPDATE_PLAN_GEOFENCE_GROUP_IDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getGeofenceGroups', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: Plan) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('geofencegroup_id', entity.geofenceGroupIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    proximityGeofenceGroupExclusions: new EntityProperty({
      label: capitalize(i18n.global.t('names.proximityGeofenceGroupExclusion', { n: 2 })),
      property: 'geofencegroup_exclusion_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'geofenceGroupExclusionIDs',
      mutation: UPDATE_PLAN_GEOFENCE_GROUP_EXCLUSION_IDS,
      editableClassName: type,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getGeofenceGroups', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: Plan) =>
        QueryParams.all(
          new FilterCollection(
            [new Filter('geofencegroup_id', entity.geofenceGroupExclusionIDs, FilterComparisonOperator.IN)],
            FilterLogicalOperator.AND,
          ),
        ),
      action,
      getExtraValidation,
    }),
    daypart: new EntityProperty({
      label: capitalize(i18n.global.t('names.daypart', { n: 2 })),
      property: 'daypart',
      type: TableColumnTypes.string,
      editablePropertyName: 'daypartRanges',
      validationPropertyName: 'daypart',
      mutation: UPDATE_PLAN_DAYPART,
      editableClassName: type,
      preMutationTransform: (text) => {
        if (text == null || text === '') return FullDaypart;
        try {
          const result = parseRanges(text);
          return convertRangesToDaypart(result.ranges);
        } catch (e) {
          return InvalidDaypart;
        }
      },
      action,
      getExtraValidation,
      canAffectAdDeliveryWhenModified: true,
      bulkInputNote: 'Note that hours are inclusive.',
      bulkInputExamples: ['Mon-Sun 6-17', 'Mon 12-16 Tue-Fri 8-9'],
      defaultValue: {
        label: daypartToRanges(FullDaypart),
        value: FullDaypart,
      },
    }),
    planFee: new EntityProperty({
      label: capitalize(i18n.global.t('names.markup', { n: 1 })),
      property: 'plan_fee',
      type: TableColumnTypes.string,
      editablePropertyName: 'planFee',
      editableClassName: type,
      action,
      getExtraValidation,
      toStringOverride: (value) => {
        return value.cpmMarkupFormatted;
      },
    }),
    maxBidCPM: new EntityProperty({
      label: capitalize(i18n.global.t('names.maxBidCPM')),
      property: 'max_bid_cpm',
      type: TableColumnTypes.currency,
      editablePropertyName: 'maxBidCPM',
      mutation: UPDATE_PLAN_MAXBIDCPM,
      editableClassName: type,
      action,
      getExtraValidation,
      preMutationTransform: (x) => (x != null ? Number.parseFloat(x) : null),
    }),
    buyFromDealsEnabled: new EntityProperty({
      label: capitalize(i18n.global.t('messages.buyFromDeal')),
      property: 'buy_from_deals_enabled',
      type: TableColumnTypes.boolean,
      editablePropertyName: 'buyFromDealsEnabled',
      editableClassName: type,
      mutation: UPDATE_PLAN_BUY_FROM_DEALS_ENABLED,
      action,
      getExtraValidation,
    }),
    deal: new EntityProperty({
      label: capitalize(i18n.global.t('names.deal')),
      property: 'dealIDFormatted',
      type: TableColumnTypes.string,
      editablePropertyName: 'dealIDFormatted',
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    deals: new EntityProperty({
      label: capitalize(i18n.global.t('names.deal', { n: 2 })),
      property: 'packageGroupDealsIDs',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'packageGroupDealsIDs',
      editableClassName: type,
      mutation: UPDATE_PLAN_PACKAGEGROUP_DEALS,
      action,
      getExtraValidation,
    }),
    externalID: new EntityProperty({
      label: capitalize(i18n.global.t('names.externalID')),
      property: 'external_id',
      type: TableColumnTypes.string,
      editablePropertyName: 'externalID',
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    mediaOwners: new EntityProperty({
      label: capitalize(i18n.global.t('names.mediaOwner', { n: 2 })),
      property: 'mediaowner_ids',
      type: TableColumnTypes.list,
      editablePropertyName: 'mediaOwnerIDs',
      editablePropertyAcceptsMultipleValues: true,
      source: getMediaOwners,
      sourceFilterProperty: 'owner_id',
    }),
    targetCriteriaWeather: new EntityProperty({
      label: capitalize(i18n.global.t('names.weatherCondition', { n: 2 })),
      property: 'targetCriteriaWeather',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaTemperature: new EntityProperty({
      label: capitalize(i18n.global.t('names.temperature', { n: 1 })),
      property: 'targetCriteriaTemperature',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaUV: new EntityProperty({
      label: capitalize(i18n.global.t('messages.uvTargeting', { n: 1 })),
      property: 'targetCriteriaUV',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaPollen: new EntityProperty({
      label: capitalize(i18n.global.t('messages.pollenTargeting', { n: 1 })),
      property: 'targetCriteriaPollen',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationWeather: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetWeatherConditions')),
      property: 'targetCriteriaDestinationWeather',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationTemperature: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetTemperature')),
      property: 'targetCriteriaDestinationTemperature',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationRain: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetRainAccumulation')),
      property: 'targetCriteriaDestinationRain',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaDestinationSnow: new EntityProperty({
      label: capitalize(i18n.global.t('messages.destinationTargetSnowAccumulation')),
      property: 'targetCriteriaDestinationSnow',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    targetCriteriaCustomEvents: new EntityProperty({
      label: capitalize(i18n.global.t('names.customEvent', { n: 2 })),
      property: 'targetCriteriaCustomEvents',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    demographics: new EntityProperty({
      label: capitalize(i18n.global.t('names.demographic', { n: 2 })),
      property: 'demographic_ids',
      type: TableColumnTypes.list,
      editablePropertyAcceptsMultipleValues: true,
      editablePropertyName: 'demographicIDs',
      mutation: UPDATE_PLAN_DEMOGRAPHIC_TARGET_IDS,
      editableClassName: type,
      source: ({ queryParams = QueryParams.all() }) => {
        return rootStore.dispatch('orgApp/getDemographics', queryParams);
      },
      action,
      preMutationTransform: (id) => (id != null ? [Number.parseInt(id, 10)] : []),
      getExtraValidation,
      sourceQueryParamsForReadOnly: (entity: Plan) =>
        QueryParams.all(FilterCollection.for('demographic_id', entity.demographicIDs)),
    }),
  };

  return Plan.entityPropertiesCached;
};
